import * as React from "react";
import { Settings2, Sun, Moon, Laptop, Pipette } from "lucide-react";
import { Button } from "./button.js";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "./sheet.js";
import { Separator } from "./separator.js";
import { Input } from "./input.js";
import { Label } from "./label.js";
import { cn } from "../lib/cn.js";
import {
  useAppearance,
  DEFAULT_CUSTOM_COLOR,
  type Appearance,
  type ColorMode,
  type ContentLayout,
  type Palette,
  type Radius,
  type Scale,
  type SidebarMode,
} from "./appearance-provider.js";

/**
 * Theme customizer
 * ----------------
 * Slide-in panel matching the user-supplied reference screenshots
 * (eduvia-refs-images/Screenshot 2026-04-26 003825.jpg + 003847.jpg)
 * with one addition: a "Custom" preset that reveals a hex/HSL color
 * picker for tenants that need their own brand color outside the
 * preset palette.
 *
 * Layout (from top of panel, mirroring ref):
 *   1. Theme preset       — segmented control + swatch preview
 *   2. Custom color       — only visible when preset === "custom"
 *   3. Scale              — XS / SM / MD / LG
 *   4. Radius             — SM / MD / LG / XL / FULL
 *   5. Color mode         — Light / Dark / System
 *   6. Content layout     — Full / Centered
 *   7. Sidebar mode       — Default / Icon
 *   8. Reset to Default   — primary button
 */

interface OptionGroupProps<T extends string> {
  label: string;
  value: T;
  options: readonly { value: T; label: string; icon?: React.ReactNode; swatch?: string }[];
  onChange: (value: T) => void;
}

function OptionGroup<T extends string>({ label, value, options, onChange }: OptionGroupProps<T>) {
  return (
    <div className="grid gap-2">
      <p className="text-muted-foreground text-xs font-medium">{label}</p>
      <div
        role="radiogroup"
        aria-label={label}
        className="border-border bg-muted/40 grid auto-cols-fr grid-flow-col gap-1.5 rounded-lg border p-1"
      >
        {options.map((opt) => {
          const selected = opt.value === value;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onChange(opt.value)}
              className={cn(
                "inline-flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-colors duration-150",
                "focus-visible:ring-ring focus-visible:outline-none focus-visible:ring-2",
                selected
                  ? "bg-card text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {opt.swatch ? (
                <span
                  className="ring-border size-2.5 rounded-full ring-1"
                  style={{ background: opt.swatch }}
                  aria-hidden="true"
                />
              ) : null}
              {opt.icon}
              <span>{opt.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

const PALETTE_OPTS: { value: Palette; label: string; swatch: string }[] = [
  { value: "default", label: "Default", swatch: "oklch(0.55 0.18 260)" },
  { value: "ocean-breeze", label: "Ocean", swatch: "oklch(0.58 0.17 270)" },
  { value: "forest-whisper", label: "Forest", swatch: "oklch(0.5 0.13 155)" },
  { value: "sunset-glow", label: "Sunset", swatch: "oklch(0.62 0.19 35)" },
  { value: "slate-pro", label: "Slate", swatch: "oklch(0.32 0.02 250)" },
];

const SCALE_OPTS: { value: Scale; label: string }[] = [
  { value: "xs", label: "XS" },
  { value: "sm", label: "SM" },
  { value: "md", label: "MD" },
  { value: "lg", label: "LG" },
];

const RADIUS_OPTS: { value: Radius; label: string }[] = [
  { value: "sm", label: "SM" },
  { value: "md", label: "MD" },
  { value: "lg", label: "LG" },
  { value: "xl", label: "XL" },
  { value: "full", label: "FULL" },
];

const MODE_OPTS: { value: ColorMode; label: string; icon: React.ReactNode }[] = [
  { value: "light", label: "Light", icon: <Sun className="size-3.5" aria-hidden="true" /> },
  { value: "dark", label: "Dark", icon: <Moon className="size-3.5" aria-hidden="true" /> },
  { value: "system", label: "System", icon: <Laptop className="size-3.5" aria-hidden="true" /> },
];

const LAYOUT_OPTS: { value: ContentLayout; label: string }[] = [
  { value: "full", label: "Full" },
  { value: "centered", label: "Centered" },
];

const SIDEBAR_OPTS: { value: SidebarMode; label: string }[] = [
  { value: "default", label: "Default" },
  { value: "icon", label: "Icon" },
];

/* -----------------------------------------------------------------------
 * Custom color picker
 * --------------------------------------------------------------------- */

const HEX_PATTERN = /^#?[0-9a-fA-F]{6}$/;

/** Quick-pick brand swatches — common SaaS palette starters. */
const QUICK_SWATCHES = [
  "#5b6cf2", // cobalt (default)
  "#0ea5e9", // sky
  "#14b8a6", // teal
  "#22c55e", // green
  "#f59e0b", // amber
  "#ef4444", // red
  "#ec4899", // pink
  "#8b5cf6", // violet
  "#0f172a", // slate
];

interface CustomColorPickerProps {
  value: string | null;
  onChange: (hex: string) => void;
}

/**
 * Inline color picker — three controls share state:
 *
 *   1. Native `<input type=color>` — opens the OS-level eyedropper +
 *      hue ring. The most familiar control on every platform.
 *   2. Hex text input — explicit, copy-pasteable, auto-prefixes `#`.
 *      Only commits to the parent state on a valid 6-char hex match
 *      so transient typing doesn't blast the theme between half-typed
 *      states (which produced flickery primary colors in tests).
 *   3. Quick swatch grid — 9 curated brand-friendly starters. Click
 *      to apply instantly.
 *
 * The live preview band beneath the controls shows the picked color
 * against three text samples (large/regular/muted) so contrast issues
 * are obvious before the user closes the panel.
 */
function CustomColorPicker({ value, onChange }: CustomColorPickerProps) {
  const seed = value ?? DEFAULT_CUSTOM_COLOR;
  const [draft, setDraft] = React.useState<string>(seed);

  // Re-sync local draft when the parent changes (e.g. quick-swatch click).
  React.useEffect(() => {
    setDraft(seed);
  }, [seed]);

  const commitDraft = (raw: string) => {
    const next = raw.startsWith("#") ? raw : `#${raw}`;
    setDraft(next);
    if (HEX_PATTERN.test(next)) {
      onChange(next.toLowerCase());
    }
  };

  return (
    <div className="border-border bg-muted/40 grid gap-3 rounded-lg border p-3">
      <div className="flex items-center gap-2">
        {/* Native color input — small chip that triggers the OS picker. */}
        <label
          className={cn(
            "border-border relative grid size-9 shrink-0 cursor-pointer place-items-center rounded-md border shadow-sm",
            "focus-within:ring-ring focus-within:ring-2",
          )}
          style={{ background: HEX_PATTERN.test(draft) ? draft : seed }}
          aria-label="Open OS color picker"
        >
          <input
            type="color"
            value={HEX_PATTERN.test(draft) ? draft : seed}
            onChange={(e) => commitDraft(e.target.value)}
            className="absolute inset-0 size-full cursor-pointer opacity-0"
          />
          <Pipette
            className="pointer-events-none size-3.5 text-white mix-blend-difference"
            aria-hidden="true"
          />
        </label>

        <div className="grid flex-1 gap-1">
          <Label htmlFor="theme-custom-hex" className="text-muted-foreground text-[11px]">
            Hex
          </Label>
          <Input
            id="theme-custom-hex"
            value={draft}
            onChange={(e) => commitDraft(e.target.value)}
            placeholder={DEFAULT_CUSTOM_COLOR}
            className="h-8 font-mono text-xs uppercase tracking-wide"
            spellCheck={false}
            autoComplete="off"
            inputMode="text"
            maxLength={7}
            aria-invalid={!HEX_PATTERN.test(draft)}
          />
        </div>
      </div>

      <div>
        <p className="text-muted-foreground mb-1.5 text-[11px] font-medium">Quick picks</p>
        <div className="grid grid-cols-9 gap-1.5" role="radiogroup" aria-label="Quick color picks">
          {QUICK_SWATCHES.map((swatch) => {
            const selected = swatch.toLowerCase() === draft.toLowerCase();
            return (
              <button
                key={swatch}
                type="button"
                role="radio"
                aria-checked={selected}
                aria-label={`Use color ${swatch}`}
                onClick={() => commitDraft(swatch)}
                className={cn(
                  "size-6 rounded-md border shadow-sm transition-transform duration-100",
                  "hover:scale-110 active:scale-95",
                  "focus-visible:ring-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1",
                  selected ? "border-foreground ring-foreground ring-2" : "border-border",
                )}
                style={{ background: swatch }}
              />
            );
          })}
        </div>
      </div>

      {/* Live preview — three text weights against the picked color so the
          user can spot foreground-contrast problems before committing. */}
      <div
        className="border-border overflow-hidden rounded-md border"
        style={{
          background: HEX_PATTERN.test(draft) ? draft : seed,
        }}
      >
        <div className="grid gap-0.5 px-3 py-2 text-[--color-primary-foreground]">
          <p className="text-sm font-semibold">Aa Quick brown fox</p>
          <p className="text-xs opacity-90">Sample button label</p>
        </div>
      </div>
    </div>
  );
}

/* -----------------------------------------------------------------------
 * Theme customizer
 * --------------------------------------------------------------------- */

export interface ThemeCustomizerProps {
  /** Override the trigger button (defaults to a settings icon button). */
  trigger?: React.ReactNode;
}

export function ThemeCustomizer({ trigger }: ThemeCustomizerProps) {
  const a = useAppearance();
  const updaters = React.useMemo(
    () => ({
      mode: (v: Appearance["mode"]) => a.set({ mode: v }),
      palette: (v: Appearance["palette"]) => a.set({ palette: v }),
      scale: (v: Appearance["scale"]) => a.set({ scale: v }),
      radius: (v: Appearance["radius"]) => a.set({ radius: v }),
      layout: (v: Appearance["layout"]) => a.set({ layout: v }),
      sidebar: (v: Appearance["sidebar"]) => a.set({ sidebar: v }),
      customColor: (hex: string) => {
        // Picking a custom color implicitly switches the palette to
        // "custom" so the user doesn't have to do it in two steps.
        a.set({ palette: "custom", customColor: hex });
      },
    }),
    [a],
  );

  // Augmented preset list — appends a "Custom" entry whose swatch is the
  // user's current customColor (or the default seed). Appears inline at
  // the end of the segmented control so the panel stays single-screen.
  const paletteOpts = React.useMemo(
    () => [
      ...PALETTE_OPTS,
      {
        value: "custom" as const,
        label: "Custom",
        swatch: a.customColor ?? DEFAULT_CUSTOM_COLOR,
      },
    ],
    [a.customColor],
  );

  return (
    <Sheet>
      <SheetTrigger asChild>
        {trigger ?? (
          <Button variant="ghost" size="icon" aria-label="Customize appearance">
            <Settings2 aria-hidden="true" />
          </Button>
        )}
      </SheetTrigger>
      <SheetContent side="right" className="w-full max-w-sm space-y-5 overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Theme</SheetTitle>
          <SheetDescription>
            Customize the look and feel. Changes are saved to your browser.
          </SheetDescription>
        </SheetHeader>

        <Separator />

        <OptionGroup
          label="Theme preset"
          value={a.palette}
          options={paletteOpts}
          onChange={updaters.palette}
        />

        {a.palette === "custom" ? (
          <div className="grid gap-2">
            <p className="text-muted-foreground text-xs font-medium">Custom color</p>
            <CustomColorPicker value={a.customColor} onChange={updaters.customColor} />
          </div>
        ) : null}

        <OptionGroup label="Scale" value={a.scale} options={SCALE_OPTS} onChange={updaters.scale} />
        <OptionGroup
          label="Radius"
          value={a.radius}
          options={RADIUS_OPTS}
          onChange={updaters.radius}
        />
        <OptionGroup
          label="Color mode"
          value={a.mode}
          options={MODE_OPTS}
          onChange={updaters.mode}
        />
        <OptionGroup
          label="Content layout"
          value={a.layout}
          options={LAYOUT_OPTS}
          onChange={updaters.layout}
        />
        <OptionGroup
          label="Sidebar mode"
          value={a.sidebar}
          options={SIDEBAR_OPTS}
          onChange={updaters.sidebar}
        />

        <Button onClick={a.reset} className="w-full" variant="default">
          Reset to Default
        </Button>
      </SheetContent>
    </Sheet>
  );
}
